import React from 'react';
import { StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useLanding } from '@/components/Landing/LandingKit';
import { isWeb, MONO } from '@/components/Landing/landingTokens';
import { Themes } from '@/constants/Colors';
import { DEFAULT_APP_THEME } from '@/constants/config';

// Use the shipping app's default theme, not the marketing page's palette.
const app = Themes[DEFAULT_APP_THEME];
const light = Themes.white;
const P = {
  bg: app.solid_color_primary,
  panel: app.solid_color_secondary,
  sidebar: app.solid_color_block,
  bubble: app.solid_color_user_bubble,
  border: app.border_color_secondary,
  inputBorder: app.border_color_input,
  ink: app.text_color_primary,
  muted: app.text_color_secondary,
  tertiary: app.text_color_tertiary,
  success: app.color_success,
};
function Small({ children, style }) {
  return <Text style={[{ fontSize: 13, lineHeight: 20, color: P.muted }, style]}>{children}</Text>;
}

export function WindowBar({ title, light: isLight = false }) {
  const colors = isLight ? light : app;
  return <View style={[styles.bar, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_tertiary }]}>
    <View aria-hidden style={{ flexDirection: 'row', gap: 6 }}>
      {['#FF5F57', '#FEBC2E', '#28C840'].map(color => <View key={color} style={{ width: 9, height: 9, borderRadius: 5, backgroundColor: color }} />)}
    </View>
    <Small style={{ color: colors.text_color_secondary, flex: 1, textAlign: 'center' }}>{title}</Small>
    <Icon name="table-columns" size={13} color={colors.text_color_secondary} />
  </View>;
}

/** Sample content, styled with the same surfaces and controls as the Mac app. */
export default function MacWorkspacePreview({ workflow }) {
  const { isCompact, isXS } = useLanding();
  return <View accessibilityLabel={`Illustrative Gab AI workspace in the app's default theme: ${workflow.project}`} style={[styles.window, isWeb && { boxShadow: '0 30px 90px rgba(0,0,0,.38), 0 0 0 1px rgba(255,255,255,.12)' }]}>
    <WindowBar title={`${workflow.project} — Gab AI`} />
    <View style={{ flexDirection: isXS ? 'column' : 'row', minHeight: isXS ? 0 : 440 }}>
      {!isCompact && <View style={styles.sidebar}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 13, marginBottom: 20 }}>
          <Text weight="bold" style={{ color: P.ink, fontSize: 18, flex: 1, letterSpacing: -0.6 }}>Gab AI</Text>
          <Icon name="magnifying-glass" size={14} color={P.ink} />
          <Icon name="gear" size={14} color={P.ink} />
        </View>
        {[['pen-to-square', 'New Chat'], ['files', 'Files'], ['robot', 'Bots'], ['grid-2', 'More']].map(([icon, label]) => <View key={label} style={styles.sideRow}>
          <Icon name={icon} size={14} color={P.ink} /><Small style={{ color: P.ink }}>{label}</Small>
        </View>)}
        <Small style={{ marginTop: 23, marginBottom: 8, fontSize: 12 }}>Collections</Small>
        <View style={styles.sideRow}><Icon name="folder-plus" size={14} color={P.muted} /><Small>New Collection</Small></View>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24, marginBottom: 8 }}>
          <Small style={{ fontSize: 12, flex: 1 }}>Projects</Small><Icon name="plus" size={11} color={P.tertiary} />
        </View>
        <View style={styles.sideRow}><Icon name="folder" size={14} color={P.ink} /><Small style={{ color: P.ink, flex: 1 }}>{workflow.project}</Small></View>
        <View style={{ paddingVertical: 9, paddingHorizontal: 10, marginLeft: 15, backgroundColor: P.panel, borderRadius: 6 }}>
          <Text numberOfLines={2} style={{ color: P.ink, fontSize: 12, lineHeight: 18 }}>{workflow.headline}</Text>
        </View>
        <View style={{ marginTop: 'auto', paddingTop: 24, gap: 6 }}>
          <Small>Connected to this Mac</Small>
          <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}><Icon name="lock" size={12} color={P.tertiary} /><Small>You control access</Small></View>
        </View>
      </View>}
      <View style={{ flex: 1.2, minWidth: 0, padding: isXS ? 20 : 22, gap: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <Small style={{ color: P.ink, flex: 1 }}>Work / {workflow.project}</Small><Small style={{ color: P.tertiary }}>Example</Small>
        </View>
        <View style={{ alignSelf: 'flex-end', maxWidth: '95%', padding: 12, borderRadius: 18, backgroundColor: P.bubble }}>
          <Text style={{ color: P.ink, fontSize: 14, lineHeight: 22 }}>{workflow.prompt}</Text>
        </View>
        <View style={{ gap: 10 }}>
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', borderBottomWidth: 1, borderColor: P.border, paddingBottom: 12 }}>
            <Icon name="circle-check" size={14} color={P.success} /><Small style={{ color: P.ink }}>Worked on this</Small>
          </View>
          {workflow.steps.map(step => <View key={step} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 9 }}>
            <View style={{ paddingTop: 4 }}><Icon name="check" size={12} color={P.tertiary} /></View><Small style={{ flex: 1 }}>{step}</Small>
          </View>)}
        </View>
        <View style={{ gap: 7 }}>{workflow.outputs.map(file => <View key={file.name} style={{ flexDirection: 'row', gap: 10, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: P.panel, alignItems: 'center' }}>
          <Icon name={file.icon} size={15} color={P.ink} /><View style={{ flex: 1 }}><Small style={{ color: P.ink }}>{file.name}</Small><Small style={{ fontSize: 12, color: P.tertiary }}>{file.kind}</Small></View><Icon name="arrow-up-right-from-square" size={12} color={P.muted} />
        </View>)}</View>
        <View style={{ marginTop: 'auto', borderWidth: 1, borderColor: P.inputBorder, borderRadius: 24, padding: 12, gap: 12 }}>
          <Small style={{ color: P.tertiary }}>Send a follow-up…</Small>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <Icon name="plus" size={16} color={P.ink} /><Icon name="folder" size={14} color={P.ink} />
            <Small style={{ flex: 1, color: P.ink }}>Work</Small><Small>Auto</Small><Icon name="chevron-down" size={9} color={P.tertiary} />
            <View style={{ width: 30, height: 30, borderRadius: 20, backgroundColor: app.solid_color_toast, alignItems: 'center', justifyContent: 'center' }}><Icon name="arrow-up" size={14} color={app.solid_color_primary} /></View>
          </View>
        </View>
      </View>
      <View style={{ flex: 1, minWidth: 0, backgroundColor: light.solid_color_tertiary, borderLeftWidth: isXS ? 0 : 1, borderTopWidth: isXS ? 1 : 0, borderColor: P.border }}>
        <View style={{ flexDirection: 'row', gap: 9, alignItems: 'center', padding: 16, borderBottomWidth: 1, borderColor: P.border, backgroundColor: P.bg }}>
          <Icon name={workflow.outputs[0].icon} size={13} color={P.muted} /><Small style={{ flex: 1 }}>{workflow.outputs[0].name}</Small><Icon name="download" size={13} color={P.muted} />
        </View>
        <View style={{ flex: 1, margin: isXS ? 14 : 18, padding: isXS ? 22 : 24, backgroundColor: light.solid_color_primary, borderWidth: 1, borderColor: light.border_color_secondary }}>
          <Small style={{ color: light.text_color_tertiary, fontSize: 12, letterSpacing: 0.5, marginBottom: 25 }}>{workflow.previewLabel}</Small>
          <Text weight="bold" style={{ color: light.text_color_primary, fontSize: isXS ? 26 : 28, lineHeight: 33, letterSpacing: -0.7 }}>{workflow.previewTitle}</Text>
          <View style={{ height: 1, backgroundColor: light.border_color_input, marginVertical: 25 }} />
          {workflow.details.map(([title, copy]) => <View key={title} style={{ marginBottom: 22, gap: 6 }}>
            <Text weight="bold" style={{ color: light.text_color_primary, fontSize: 14 }}>{title}</Text><Small style={{ color: light.text_color_secondary, fontSize: 14, lineHeight: 23 }}>{copy}</Small>
          </View>)}
          <View style={{ marginTop: 'auto', paddingTop: 20, borderTopWidth: 1, borderColor: light.border_color_secondary }}><Small style={{ color: light.text_color_tertiary }}>Made with your context. Ready for your review.</Small></View>
        </View>
      </View>
    </View>
  </View>;
}

export function CodePreview() {
  return <View style={styles.window} accessibilityLabel="Illustrative code change and test output">
    <WindowBar title="contact.jsx · Changes" />
    <View style={{ padding: 24, gap: 8 }}>
      <Small style={{ marginBottom: 7 }}>A contact page that fits your project.</Small>
      {['+ export function ContactPage() {', '+   return (', '+     <ContactForm', '+       title="Let’s make something."', '+       onSubmit={sendMessage}', '+     />', '+   );', '+ }'].map(line => <Text key={line} style={{ fontFamily: MONO, color: P.ink, fontSize: 13, lineHeight: 24 }}>{line}</Text>)}
      <View style={{ borderTopWidth: 1, borderColor: P.border, marginTop: 14, paddingTop: 18, gap: 6 }}><Small style={{ color: P.ink }}>Local tests & builds</Small><Small>Run your installed checks. Read the output.</Small><Small>Review changes → Refine → Keep building</Small></View>
    </View>
  </View>;
}

export function RecoveryPreview() {
  return <View style={[styles.window, { backgroundColor: light.solid_color_primary, borderColor: light.border_color_secondary }]} accessibilityLabel="Illustrative saved edit review">
    <WindowBar title="Changes · project-brief.md" light />
    <View style={{ padding: 22, gap: 16 }}>
      <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}><Icon name="clock-rotate-left" color={light.text_color_secondary} size={16} /><Text weight="medium" style={{ color: light.text_color_primary, fontSize: 14 }}>Original saved on this Mac</Text></View>
      {[['# Project brief', light.solid_color_tertiary, light.text_color_secondary], ['− Goal: update the website.', '#FFF0ED', '#9C4433'], ['+ Make the offer easier to understand.', '#E9F4E6', '#285F36'], ['+ Help visitors take the next step.', '#E9F4E6', '#285F36']].map(([line, bg, ink]) => <Text key={line} style={{ fontFamily: MONO, fontSize: 13, lineHeight: 22, padding: 10, borderRadius: 5, backgroundColor: bg, color: ink }}>{line}</Text>)}
      <Small style={{ color: light.text_color_tertiary }}>Review the edit. Restore a supported saved version.</Small>
    </View>
  </View>;
}
const styles = StyleSheet.create({
  window: { width: '100%', borderWidth: 1, borderColor: P.border, borderRadius: 14, overflow: 'hidden', backgroundColor: P.bg },
  bar: { minHeight: 43, paddingHorizontal: 16, paddingVertical: 12, flexDirection: 'row', alignItems: 'center', gap: 12, borderBottomWidth: 1 },
  sidebar: { width: 185, padding: 16, backgroundColor: P.sidebar, borderRightWidth: 1, borderColor: P.border },
  sideRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8 },
});
